import { Ref } from '@vue/composition-api';
import { useLocalStorage } from '@vueuse/core';
import {
	FALLBACK_LOCALE,
	LOCALE_STRING_NAMES,
	PhoenixLocale,
} from '../model/locale';
import { getBrowserLocales } from '../util/navigatorLanguages';
import { isLocale } from '../util/types';

const LOCALE_SETTING_KEY = 'phx_user_locale';

export interface LocalSettingsStore {
	/**
	 * This will tell us the user's setting value, which might be DEVICE_DEFAULT
	 */
	getLocaleSetting(): PhoenixLocale;
	/**
	 * This will calculate the actual locale to display, taking into account the user's
	 * device settings if DEVICE_DEFAULT is selected
	 */
	getDisplayLocale(): Exclude<PhoenixLocale, PhoenixLocale.DEVICE_DEFAULT>;
	setLocaleSetting(locale: PhoenixLocale): void;
}

export function getI18nSettingsStore(): LocalSettingsStore {
	const localeSetting: Ref<PhoenixLocale> = useLocalStorage<PhoenixLocale>(LOCALE_SETTING_KEY, PhoenixLocale.DEVICE_DEFAULT);

	function getLocaleSetting() {
		const val = localeSetting.value;
		return val === getDisplayLocale() ? val : PhoenixLocale.DEVICE_DEFAULT;
	}

	function setLocaleSetting(locale: PhoenixLocale): void {
		localeSetting.value = locale;
	}

	function getDisplayLocale(): Exclude<PhoenixLocale, PhoenixLocale.DEVICE_DEFAULT> {
		const val = localeSetting.value;
		if (!val || !isLocale(val) || val === PhoenixLocale.DEVICE_DEFAULT) {
			return getBrowserLocale(LOCALE_STRING_NAMES, FALLBACK_LOCALE);
		}
		return val;
	}

	return {
		getLocaleSetting,
		getDisplayLocale,
		setLocaleSetting,
	};
}

function getBrowserLocale(
	supportedLocales: string[],
	fallbackLocale: Exclude<PhoenixLocale, PhoenixLocale.DEVICE_DEFAULT>,
): Exclude<PhoenixLocale, PhoenixLocale.DEVICE_DEFAULT> {
	const browserLocales = getBrowserLocales();
	const exactResult = browserLocales
		.find((val) => supportedLocales.includes(val));
	if (exactResult && isLocale(exactResult) && exactResult !== PhoenixLocale.DEVICE_DEFAULT) {
		return exactResult;
	}
	const partialResult = browserLocales
		.map((val) => val.toLocaleLowerCase().substr(0, 2))
		.find((val) => supportedLocales.includes(val.substr(0, 2)));
	if (partialResult && isLocale(partialResult) && partialResult !== PhoenixLocale.DEVICE_DEFAULT) {
		return partialResult;
	}
	return fallbackLocale;
}
